import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Grid, IconButton, MenuItem, Paper, Radio, RadioGroup,
    Stack, Switch, TextField, Typography
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { Localized, useLocalization } from "@fluent/react";
import { DocumentHeader } from "../documentsApi";
import { DateFromModeEnum, DateToModeEnum, IntervalUnitEnum } from "../../common/commonTypes";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { updateDocumentHeader } from "../documentSlice";
import { updateQmsDateFrom, updateQmsDateTo, updateQmsReview } from "../documentsQmsApi";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";

interface DocumentQMSDetailsProps {
    document: DocumentHeader;
}

const formatDateValue = (value?: Date | string | null) => {
    if (!value) {
        return <Localized id="not-applicable-short">N/A</Localized>;
    }

    return new Date(value).toLocaleDateString();
};

const toDateInputValue = (value?: Date | string | null) => {
    if (!value) {
        return "";
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return "";
    }

    return parsedDate.toISOString().slice(0, 10);
};

const dateInputToDate = (value: string): Date | undefined => {
    if (!value) {
        return undefined;
    }

    const parsedDate = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) {
        return undefined;
    }

    return parsedDate;
};

const addInterval = (baseDateValue: string, intervalValue: number, intervalUnit: IntervalUnitEnum) => {
    const baseDate = new Date(`${baseDateValue}T00:00:00`);
    if (Number.isNaN(baseDate.getTime()) || intervalValue <= 0) {
        return null;
    }

    const result = new Date(baseDate);
    switch (intervalUnit) {
        case "DAYS":
            result.setDate(result.getDate() + intervalValue);
            break;
        case "MONTHS":
            result.setMonth(result.getMonth() + intervalValue);
            break;
        case "YEARS":
            result.setFullYear(result.getFullYear() + intervalValue);
            break;
        default:
            return null;
    }

    return result.toISOString();
};

const formatIntervalUnit = (unit: IntervalUnitEnum | null | undefined, l10n: ReturnType<typeof useLocalization>["l10n"]) => {
    if (!unit) {
        return l10n.getString("not-applicable-short", null, "N/A");
    }

    switch (unit) {
        case "DAYS":
            return l10n.getString("interval-unit-days", null, "Days");
        case "MONTHS":
            return l10n.getString("interval-unit-months", null, "Months");
        case "YEARS":
            return l10n.getString("interval-unit-years", null, "Years");
        default:
            return l10n.getString("not-applicable-short", null, "N/A");
    }
};

// ─── ValidityRow ─────────────────────────────────────────────────────────────

type ValidityRowProps = {
    label: React.ReactNode;
    dateValue: React.ReactNode;
    modeLabel: React.ReactNode;
    onEdit?: () => void;
    last?: boolean;
};

const ValidityRow = ({ label, dateValue, modeLabel, onEdit, last = false }: ValidityRowProps) => (
    <Grid
        container
        spacing={1}
        alignItems="center"
        sx={{ py: 0.75, borderBottom: last ? "none" : (t) => `1px dashed ${t.palette.divider}` }}
    >
        <Grid item xs={12} sm={4}>
            <Box sx={(t) => ({
                bgcolor: t.palette.grey[100],
                border: `1px solid ${t.palette.grey[200]}`,
                borderRadius: 1, px: 1, py: 0.75,
                display: "flex", alignItems: "center",
            })}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">{label}</Typography>
            </Box>
        </Grid>
        <Grid item xs sm>
            <Box sx={(t) => ({
                border: `1px solid ${t.palette.grey[200]}`,
                bgcolor: t.palette.grey[50],
                borderRadius: 1, px: 1.25, py: 0.75,
                display: "flex", alignItems: "center",
            })}>
                <Typography variant="body1" fontWeight={600}>{dateValue}</Typography>
            </Box>
        </Grid>
        <Grid item sx={{ width: { xs: "100%", sm: 260 }, flexShrink: 0 }}>
            <Box sx={(t) => ({
                border: `1px solid ${t.palette.grey[200]}`,
                bgcolor: t.palette.grey[50],
                borderRadius: 1, px: 1.25, py: 0.75,
                display: "flex", alignItems: "flex-start",
                minHeight: 52,
            })}>
                <Typography variant="body2" color="text.secondary">{modeLabel}</Typography>
            </Box>
        </Grid>
        {onEdit && (
            <Grid item xs="auto">
                <IconButton size="small" onClick={onEdit} disabled={!onEdit}>
                    <Edit fontSize="small" />
                </IconButton>
            </Grid>
        )}
    </Grid>
);

type ReadonlyValueRowProps = {
    label: React.ReactNode;
    value: React.ReactNode;
    onEdit?: () => void;
    last?: boolean;
};

const ReadonlyValueRow = ({ label, value, onEdit, last = false }: ReadonlyValueRowProps) => (
    <Grid
        container
        spacing={1}
        alignItems="center"
        sx={{ py: 0.75, borderBottom: last ? "none" : (t) => `1px dashed ${t.palette.divider}` }}
    >
        <Grid item xs={12} sm={4}>
            <Box sx={(t) => ({
                bgcolor: t.palette.grey[100],
                border: `1px solid ${t.palette.grey[200]}`,
                borderRadius: 1, px: 1, py: 0.75,
                display: "flex", alignItems: "center",
            })}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">{label}</Typography>
            </Box>
        </Grid>
        <Grid item xs>
            <Box sx={(t) => ({
                border: `1px solid ${t.palette.grey[200]}`,
                bgcolor: t.palette.grey[50],
                borderRadius: 1, px: 1.25, py: 0.75,
                display: "flex", alignItems: "center",
            })}>
                <Typography variant="body1" fontWeight={600}>{value}</Typography>
            </Box>
        </Grid>
        {onEdit && (
            <Grid item xs="auto">
                <IconButton size="small" onClick={onEdit} disabled={!onEdit}>
                    <Edit fontSize="small" />
                </IconButton>
            </Grid>
        )}
    </Grid>
);

// ─── EditEffectiveFromDialog ──────────────────────────────────────────────────

type EditEffectiveFromDialogProps = {
    open: boolean;
    initialDate: string;
    initialMode: DateFromModeEnum;
    onClose: () => void;
    onSave: (date: Date | undefined, mode: DateFromModeEnum) => void;
    loading?: boolean;
};

const EditEffectiveFromDialog = ({ open, initialDate, initialMode, onClose, onSave, loading = false }: EditEffectiveFromDialogProps) => {
    const { l10n } = useLocalization();
    const [mode, setMode] = useState<DateFromModeEnum>(initialMode);
    const [dateInput, setDateInput] = useState<string>(initialDate);

    useEffect(() => {
        if (open) {
            setMode(initialMode);
            setDateInput(initialDate);
        }
    }, [open, initialDate, initialMode]);

    const handleSave = () => {
        const date = mode === "MANUAL" ? dateInputToDate(dateInput) : undefined;
        onSave(date, mode);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {l10n.getString("company-document-type-qms-effective-from-label", null, "Data obowiązuje od")}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as DateFromModeEnum)}>
                        <FormControlLabel
                            value="MANUAL"
                            control={<Radio />}
                            label={l10n.getString("company-document-type-qms-effective-from-manual", null, "Manualna")}
                        />
                        <FormControlLabel
                            value="PUBLICATION"
                            control={<Radio />}
                            label={l10n.getString("company-document-type-qms-effective-from-publication", null, "Zgodna z datą publikacji")}
                        />
                    </RadioGroup>
                    {mode === "MANUAL" && (
                        <TextField
                            label={l10n.getString("company-document-type-qms-effective-from-label", null, "Data")}
                            type="date"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={dateInput}
                            onChange={(e) => setDateInput(e.target.value)}
                            fullWidth
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <SaveButton loading={loading} onClick={handleSave} />
                <CancelButton loading={loading} onClick={onClose} />
            </DialogActions>
        </Dialog>
    );
};

// ─── EditEffectiveToDialog ────────────────────────────────────────────────────

type EditEffectiveToDialogProps = {
    open: boolean;
    initialDate: string;
    initialMode: DateToModeEnum;
    initialIntervalValue: number;
    initialIntervalUnit: IntervalUnitEnum;
    effectiveFrom: string;
    onClose: () => void;
    onSave: (date: Date | undefined, mode: DateToModeEnum, intervalValue: number, intervalUnit: IntervalUnitEnum) => void;
    loading?: boolean;
};

const intervalUnitOptions: IntervalUnitEnum[] = ["DAYS", "MONTHS", "YEARS"];

const EditEffectiveToDialog = ({
    open, initialDate, initialMode, initialIntervalValue, initialIntervalUnit, effectiveFrom, onClose, onSave, loading = false,
}: EditEffectiveToDialogProps) => {
    const { l10n } = useLocalization();
    const [mode, setMode] = useState<DateToModeEnum>(initialMode);
    const [dateInput, setDateInput] = useState<string>(initialDate);
    const [intervalValue, setIntervalValue] = useState<number>(initialIntervalValue);
    const [intervalUnit, setIntervalUnit] = useState<IntervalUnitEnum>(initialIntervalUnit);

    useEffect(() => {
        if (open) {
            setMode(initialMode);
            setDateInput(initialDate);
            setIntervalValue(initialIntervalValue);
            setIntervalUnit(initialIntervalUnit);
        }
    }, [open, initialDate, initialMode, initialIntervalValue, initialIntervalUnit]);

    const computedDate = useMemo(() => {
        if (mode !== "INTERVALFROM") return null;
        return addInterval(effectiveFrom, intervalValue, intervalUnit);
    }, [effectiveFrom, intervalValue, intervalUnit, mode]);

    const handleSave = () => {
        let date: Date | undefined;
        if (mode === "MANUAL") {
            date = dateInputToDate(dateInput);
        } else if (computedDate) {
            date = new Date(computedDate);
        }
        onSave(date, mode, intervalValue, intervalUnit);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {l10n.getString("company-document-type-qms-effective-to-label", null, "Data obowiązuje do")}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as DateToModeEnum)}>
                        <FormControlLabel
                            value="MANUAL"
                            control={<Radio />}
                            label={l10n.getString("company-document-type-qms-effective-to-manual", null, "Manualna")}
                        />
                        <FormControlLabel
                            value="INTERVALFROM"
                            control={<Radio />}
                            label={l10n.getString("company-document-type-qms-effective-to-interval", null, "Według interwału od daty od")}
                        />
                    </RadioGroup>
                    {mode === "MANUAL" && (
                        <TextField
                            label={l10n.getString("company-document-type-qms-effective-to-label", null, "Data")}
                            type="date"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={dateInput}
                            onChange={(e) => setDateInput(e.target.value)}
                            fullWidth
                        />
                    )}
                    {mode === "INTERVALFROM" && (
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1.5}>
                                <TextField
                                    label={l10n.getString("company-document-type-qms-validity-interval-value", null, "Wartość interwału")}
                                    type="number"
                                    size="small"
                                    inputProps={{ min: 1 }}
                                    value={intervalValue}
                                    onChange={(e) => setIntervalValue(Math.max(1, Number(e.target.value) || 1))}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    select
                                    label={l10n.getString("company-document-type-qms-validity-interval-unit", null, "Jednostka")}
                                    value={intervalUnit}
                                    onChange={(e) => setIntervalUnit(e.target.value as IntervalUnitEnum)}
                                    size="small"
                                    sx={{ flex: 1 }}
                                >
                                    {intervalUnitOptions.map((unit) => (
                                        <MenuItem key={unit} value={unit}>
                                            {formatIntervalUnit(unit, l10n)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                            {computedDate && (
                                <Typography variant="body2" color="text.secondary">
                                    {l10n.getString("qms-computed-date", null, "Wyliczona data:")} {new Date(computedDate).toLocaleDateString()}
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <SaveButton loading={loading} onClick={handleSave} />
                <CancelButton loading={loading} onClick={onClose} />
            </DialogActions>
        </Dialog>
    );
};

type EditReviewSettingsDialogProps = {
    open: boolean;
    initialRequiresReview: boolean;
    initialIntervalValue: number;
    initialIntervalUnit: IntervalUnitEnum;
    onClose: () => void;
    onSave: (requiresReview: boolean, intervalValue: number, intervalUnit: IntervalUnitEnum) => void;
    loading?: boolean;
};

const EditReviewSettingsDialog = ({
    open,
    initialRequiresReview,
    initialIntervalValue,
    initialIntervalUnit,
    onClose,
    onSave,
    loading = false,
}: EditReviewSettingsDialogProps) => {
    const { l10n } = useLocalization();
    const [requiresReview, setRequiresReview] = useState<boolean>(initialRequiresReview);
    const [intervalValue, setIntervalValue] = useState<number>(initialIntervalValue);
    const [intervalUnit, setIntervalUnit] = useState<IntervalUnitEnum>(initialIntervalUnit);

    useEffect(() => {
        if (open) {
            setRequiresReview(initialRequiresReview);
            setIntervalValue(initialIntervalValue);
            setIntervalUnit(initialIntervalUnit);
        }
    }, [open, initialRequiresReview, initialIntervalValue, initialIntervalUnit]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {l10n.getString("company-document-type-qms-review-section", null, "Przeglądy dokumentu")}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <FormControlLabel
                        control={<Switch checked={requiresReview} onChange={(e) => setRequiresReview(e.target.checked)} />}
                        label={l10n.getString("company-document-type-qms-requires-review", null, "Wymagane przeglądy dokumentu")}
                    />
                    {requiresReview && (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <TextField
                                label={l10n.getString("company-document-type-qms-review-interval-value", null, "Wartość interwału przeglądu")}
                                type="number"
                                size="small"
                                inputProps={{ min: 1 }}
                                value={intervalValue}
                                onChange={(e) => setIntervalValue(Math.max(1, Number(e.target.value) || 1))}
                                sx={{ minWidth: 180 }}
                            />
                            <TextField
                                select
                                label={l10n.getString("company-document-type-qms-review-interval-unit", null, "Jednostka interwału przeglądu")}
                                value={intervalUnit}
                                onChange={(e) => setIntervalUnit(e.target.value as IntervalUnitEnum)}
                                size="small"
                                sx={{ minWidth: 180 }}
                            >
                                {intervalUnitOptions.map((unit) => (
                                    <MenuItem key={unit} value={unit}>
                                        {formatIntervalUnit(unit, l10n)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <SaveButton loading={loading} onClick={() => onSave(requiresReview, intervalValue, intervalUnit)} />
                <CancelButton loading={loading} onClick={onClose} />
            </DialogActions>
        </Dialog>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const DocumentQMSDetails = ({ document }: DocumentQMSDetailsProps) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();

    const isEditable = document.status === 'N';
    const [dialog, setDialog] = useState<"none" | "effectiveFrom" | "effectiveTo" | "reviewSettings">("none");
    const [loading, setLoading] = useState<boolean>(false);

    // ── Reviews state ────────────────────────────────────────────────────────
    const [requiresReview, setRequiresReview] = useState<boolean>(document.requiresReview);
    const [reviewIntervalValue, setReviewIntervalValue] = useState<number>(document.reviewIntervalValue || 12);
    const [reviewIntervalUnit, setReviewIntervalUnit] = useState<IntervalUnitEnum>(document.reviewIntervalUnit || "MONTHS");
    const validityIntervalValue = document.validityIntervalValue || 12;
    const validityIntervalUnit = document.validityIntervalUnit || "MONTHS";

    const patchHeader = (patch: Partial<DocumentHeader>) => {
        dispatch(updateDocumentHeader({ ...document, ...patch }));
    };

    useEffect(() => {
        setRequiresReview(document.requiresReview);
        setReviewIntervalValue(document.reviewIntervalValue || 12);
        setReviewIntervalUnit(document.reviewIntervalUnit || "MONTHS");
    }, [document]);

    const computedReviewDueDateIso = useMemo(() => {
        if (!requiresReview) return null;
        const baseDate = toDateInputValue(document.effectiveFrom);
        if (!baseDate) return null;
        return addInterval(baseDate, reviewIntervalValue, reviewIntervalUnit);
    }, [document.effectiveFrom, requiresReview, reviewIntervalValue, reviewIntervalUnit]);

    useEffect(() => {
        if (!requiresReview || !computedReviewDueDateIso) return;
        const computedValue = computedReviewDueDateIso.slice(0, 10);
        if (toDateInputValue(document.reviewDueDate) !== computedValue) {
            patchHeader({ reviewDueDate: new Date(computedReviewDueDateIso) });
        }
    }, [computedReviewDueDateIso, document.reviewDueDate, requiresReview]);

    // ── Mode labels for validity section display ──────────────────────────────
    const fromModeLabel = document.effectiveFromMode === "MANUAL"
        ? l10n.getString("company-document-type-qms-effective-from-manual", null, "Manualna")
        : l10n.getString("company-document-type-qms-effective-from-publication", null, "Zgodna z datą publikacji");

    const toModeLabelNode: React.ReactNode = (() => {
        const text = document.effectiveToMode === "MANUAL"
            ? l10n.getString("company-document-type-qms-effective-to-manual", null, "Manualna")
            : l10n.getString("company-document-type-qms-effective-to-interval", null, "Według interwału");
        if (document.effectiveToMode === "INTERVALFROM") {
            return (
                <Stack>
                    <span>{text}</span>
                    <Typography variant="caption" color="text.secondary">
                        {validityIntervalValue} {formatIntervalUnit(validityIntervalUnit, l10n)}
                    </Typography>
                </Stack>
            );
        }
        return text;
    })();

    const nextReviewDateValue = computedReviewDueDateIso
        ? formatDateValue(computedReviewDueDateIso)
        : formatDateValue(document.reviewDueDate);

    const reviewIntervalLabel = requiresReview
        ? `${reviewIntervalValue} ${formatIntervalUnit(reviewIntervalUnit, l10n)}`
        : l10n.getString("not-applicable-short", null, "N/A");

    const reviewHistoryRows = useMemo(
        () => ([
            {
                date: formatDateValue(document.lastReviewDate),
                comment: l10n.getString("not-applicable-short", null, "N/A"),
            },
        ]),
        [document.lastReviewDate, l10n]
    );

    return (
        <Box mt={2}>
            {/* ── Validity section (read-only + pencil dialogs, grey header) ── */}
            <Paper variant="outlined" sx={{ mt: 2, overflow: "hidden" }}>
                <Box sx={{ px: 2, py: 1, bgcolor: (t) => t.palette.grey[200] }}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                        {l10n.getString("company-document-type-qms-validity-section", null, "Okres obowiązywania dokumentu")}
                    </Typography>
                </Box>
                <Box sx={{ px: 0.5 }}>
                    <ValidityRow
                        label={<Localized id="company-document-type-qms-effective-from-label">Data obowiązuje od</Localized>}
                        dateValue={formatDateValue(document.effectiveFrom)}
                        modeLabel={fromModeLabel}
                        onEdit={isEditable ? () => setDialog("effectiveFrom") : undefined}
                    />
                    <ValidityRow
                        label={<Localized id="company-document-type-qms-effective-to-label">Data obowiązuje do</Localized>}
                        dateValue={formatDateValue(document.effectiveTo)}
                        modeLabel={toModeLabelNode}
                        onEdit={isEditable ? () => setDialog("effectiveTo") : undefined}
                        last
                    />
                </Box>
            </Paper>

            {/* ── Reviews section ── */}
            <Paper variant="outlined" sx={{ mt: 2, overflow: "hidden" }}>
                <Box sx={{ px: 2, py: 1, bgcolor: (t) => t.palette.grey[200] }}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                        {l10n.getString("company-document-type-qms-review-section", null, "Przeglądy dokumentu")}
                    </Typography>
                </Box>
                <Box sx={{ px: 0.5 }}>
                    <ReadonlyValueRow
                        label={l10n.getString("company-document-type-qms-requires-review", null, "Wymagane przeglądy")}
                        value={requiresReview ? l10n.getString("yes", null, "Tak") : l10n.getString("no", null, "Nie")}
                        onEdit={isEditable ? () => setDialog("reviewSettings") : undefined}
                    />
                    <ReadonlyValueRow
                        label={l10n.getString("company-document-type-qms-review-interval-value", null, "Interwał przeglądów")}
                        value={reviewIntervalLabel}
                        onEdit={isEditable ? () => setDialog("reviewSettings") : undefined}
                    />
                    <ReadonlyValueRow
                        label={l10n.getString("qms-review-due-date", null, "Termin następnego przeglądu")}
                        value={nextReviewDateValue}
                        last
                    />
                </Box>
            </Paper>

            {/* ── History section ── */}
            <Paper variant="outlined" sx={{ mt: 2, overflow: "hidden" }}>
                <Box sx={{ px: 2, py: 1, bgcolor: (t) => t.palette.grey[200] }}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                        {l10n.getString("document-review-history", null, "Historia przeglądów")}
                    </Typography>
                </Box>
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>Data</Typography>
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>Komentarz</Typography>
                        </Grid>
                    </Grid>
                    {reviewHistoryRows.map((row, idx) => (
                        <Grid
                            key={idx}
                            container
                            spacing={1}
                            sx={{ py: 0.75, borderTop: (t) => `1px dashed ${t.palette.divider}` }}
                        >
                            <Grid item xs={4}>
                                <Typography variant="body2">{row.date}</Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography variant="body2" color="text.secondary">{row.comment}</Typography>
                            </Grid>
                        </Grid>
                    ))}
                    {reviewHistoryRows.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {l10n.getString("not-applicable-short", null, "N/A")}
                        </Typography>
                    )}
                </Box>
            </Paper>

            {/* ── Dialogs ── */}
            <EditEffectiveFromDialog
                open={dialog === "effectiveFrom"}
                initialDate={toDateInputValue(document.effectiveFrom)}
                initialMode={document.effectiveFromMode ?? "MANUAL"}
                onClose={() => setDialog("none")}
                loading={loading}
                onSave={async (date, mode) => {
                    setLoading(true);
                    try {
                        const result = await dispatch(updateQmsDateFrom({
                            documentId: document.id,
                            dto: {
                                mode,
                                ...(date ? { effectiveFrom: date.toISOString() } : {}),
                            },
                        }))
                        if (updateQmsDateFrom.fulfilled.match(result)) {
                            dispatch(updateDocumentHeader(result.payload));
                        }
                    } finally {
                        setLoading(false);
                        setDialog("none");
                    }
                }}
            />
            <EditEffectiveToDialog
                open={dialog === "effectiveTo"}
                initialDate={toDateInputValue(document.effectiveTo)}
                initialMode={document.effectiveToMode ?? "MANUAL"}
                initialIntervalValue={validityIntervalValue}
                initialIntervalUnit={validityIntervalUnit}
                effectiveFrom={toDateInputValue(document.effectiveFrom)}
                onClose={() => setDialog("none")}
                loading={loading}
                onSave={async (date, mode, iv, iu) => {
                    setLoading(true);
                    try {
                        const result = await dispatch(updateQmsDateTo({
                            documentId: document.id,
                            dto: {
                                mode,
                                ...(date ? { effectiveTo: date.toISOString() } : {}),
                                validityIntervalValue: iv,
                                validityIntervalUnit: iu,
                            },
                        }))
                        if (updateQmsDateTo.fulfilled.match(result)) {
                            dispatch(updateDocumentHeader(result.payload));
                        }
                    } finally {
                        setLoading(false);
                        setDialog("none");
                    }
                }}
            />
            <EditReviewSettingsDialog
                open={dialog === "reviewSettings"}
                initialRequiresReview={requiresReview}
                initialIntervalValue={reviewIntervalValue}
                initialIntervalUnit={reviewIntervalUnit}
                onClose={() => setDialog("none")}
                loading={loading}
                onSave={async (newRequiresReview, newIntervalValue, newIntervalUnit) => {
                    setLoading(true);
                    try {
                        const result = await dispatch(updateQmsReview({
                            documentId: document.id,
                            dto: {
                                requiresReview: newRequiresReview,
                                reviewIntervalValue: newRequiresReview ? newIntervalValue : undefined,
                                reviewIntervalUnit: newRequiresReview ? newIntervalUnit : undefined,
                                reviewDueDate: newRequiresReview ? undefined : null,
                            },
                        }))
                        if (updateQmsReview.fulfilled.match(result)) {
                            dispatch(updateDocumentHeader(result.payload));
                        }
                    } finally {
                        setLoading(false);
                        setDialog("none");
                    }
                }}
            />
        </Box>
    );
};